import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Icon from '../../../atoms/icons/Icon';
import BooksOutlineButton from '../../merchant/resources/book/common/buttons/BooksOutlineButton';
import css from './style.module.scss';
import { SubmitButton } from '../../../atoms/SubmitButton';
import { buildUrl } from 'src/utils/request-url';

export const VerificationContext = {
  RESERVATION: 'RESERVATION',
  PRODUCT: 'PRODUCT',
} as const;

export type VerificationContext =
  (typeof VerificationContext)[keyof typeof VerificationContext];

interface Props {
  email: string;
  isValidating: boolean;
  isJustCreateUser: boolean;
  verificationContext: VerificationContext;
  handleSubmit: React.MouseEventHandler;
}

const Title: FC<{
  isJustCreateUser: boolean;
}> = ({ isJustCreateUser }) => {
  return (
    <p className={`${css.Paragraph} ${css.Title}`}>
      {isJustCreateUser ? (
        <FormattedMessage id="emailVerification.sentEmailToVerify" />
      ) : (
        <FormattedMessage id="emailVerification.hasNotBeenVerification" />
      )}
    </p>
  );
};

const Description: FC<{
  email: string;
  isJustCreateUser: boolean;
}> = ({ isJustCreateUser, email }) => {
  return (
    <p className={`${css.Paragraph} ${css.Description}`}>
      {isJustCreateUser ? (
        <FormattedMessage
          id="emailVerification.sentEmailOnCreateUser"
          values={{
            email: <span className={css.Email}>{email}</span>,
          }}
        />
      ) : (
        <FormattedMessage
          id="emailVerification.willSendEmailOnLogin"
          values={{
            email: <span className={css.Email}>{email}</span>,
          }}
        />
      )}
    </p>
  );
};

const Notes: FC<{
  isJustCreateUser: boolean;
  context: VerificationContext;
}> = ({ isJustCreateUser, context }) => {
  return (
    <p className={`${css.Paragraph} ${css.Notes}`}>
      <span className={css.Underline}>
        {isJustCreateUser && context === VerificationContext.RESERVATION && (
          <FormattedMessage id="emailVerification.createUserOnReservation" />
        )}
        {isJustCreateUser && context === VerificationContext.PRODUCT && (
          <FormattedMessage id="emailVerification.createUserOnPurchase" />
        )}
        {!isJustCreateUser && context === VerificationContext.RESERVATION && (
          <FormattedMessage id="emailVerification.canRestartReservationAfterVerfication" />
        )}
        {!isJustCreateUser && context === VerificationContext.PRODUCT && (
          <FormattedMessage id="emailVerification.canRestartPurchaseAfterVerfication" />
        )}
      </span>
    </p>
  );
};

const EmailVerification = ({
  email,
  isValidating,
  isJustCreateUser,
  handleSubmit,
  verificationContext,
}: Props) => {
  const intl = useIntl();
  return (
    <div className={css.EmailVerification}>
      <div className={css.Main}>
        <div className={css.Mail}>
          <Icon name="mail" className={css.Icon} />
        </div>
        <Title isJustCreateUser={isJustCreateUser} />
        {!isJustCreateUser && (
          <SubmitButton
            className={css.SubmitButton}
            onClick={handleSubmit}
            disabled={isValidating}
          >
            {intl.formatMessage({
              id: 'emailVerification.sendEmail',
            })}
          </SubmitButton>
        )}
        <Description email={email} isJustCreateUser={isJustCreateUser} />
        <Notes
          isJustCreateUser={isJustCreateUser}
          context={verificationContext}
        />
      </div>

      <div className={css.Caution}>
        <p className={css.Title}>
          {intl.formatMessage({
            id: 'emailVerification.dontReceiveEmail',
          })}
        </p>
        <p className={css.Description}>
          {intl.formatMessage({
            id: 'emailVerification.dontReceiveEmailDescription',
          })}
        </p>
        <p className={css.Detail}>
          <ul className={css.padding_x_18}>
            <li>
              <FormattedMessage
                id="emailVerification.pleaseCheckEmailIsCorrect"
                values={{
                  email: <span className={css.Email}>{email}</span>,
                  emailEditLink: (
                    <a
                      href={buildUrl('/u/settings/email/edit')}
                      className={css.Link}
                    >
                      {intl.formatMessage({ id: 'emailVerification.here' })}
                    </a>
                  ),
                }}
              />
            </li>
            <li>
              <FormattedMessage id="emailVerification.pleaseCheckEmailAllowSetting" />
            </li>
            <li>
              <FormattedMessage id="emailVerification.pleaseCheckMeiwakuMail" />
            </li>
            <li>
              <FormattedMessage id="emailVerification.mayTakeTimeToSendEmail" />
            </li>
          </ul>
        </p>
        {isJustCreateUser && (
          <BooksOutlineButton
            type="button"
            className={css.resendButton}
            onClick={handleSubmit}
            disabled={isValidating}
            children={intl.formatMessage({
              id: 'emailVerification.resendEmail',
            })}
          />
        )}
        <div className={css.mt_30}>
          <div>
            <a
              className={css.help_page_text}
              href="https://reserve-faq.stores.jp/hc/ja/articles/28754125263129"
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className={css.flex_center}>
                <div>
                  {intl.formatMessage({ id: 'emailVerification.helpPageHere' })}
                </div>
                <div className={css.external_link_icon_wrapper}>
                  <Icon
                    name="external-link"
                    className={css.external_link_icon}
                  />
                </div>
              </div>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
